import './CoralQuoteLarge.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { Baseplate, StudRange } from '@websrc/App/Components/Baseplate/Baseplate'
import { BrickColor } from '@websrc/App/Components/Brick/Brick'
import { mapCoralWebQuote } from '@websrc/DataMappers/mapArticleBody'
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'

type CoralQuoteLargeProps = ReturnType<typeof mapCoralWebQuote>

export const CoralQuoteLarge = ({
  quote,
  quotedPerson,
  image,
  visualsColors,
}: CoralQuoteLargeProps) => {
  const isTablet = useIsWindowBiggerThan(719)
  const isDesktop = useIsWindowBiggerThan(1079)
  const isWidescreen = useIsWindowBiggerThan(1439)
  const isUltraWide = useIsWindowBiggerThan(1919)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const [dynamicRows, setDynamicRows] = useState(1)
  const fixedUnit = useMemo(() => (isDesktop ? 24 : 16), [isDesktop])

  useLayoutEffect(() => {
    function recalculateRows() {
      if (wrapperRef.current) {
        const height = wrapperRef.current.offsetHeight
        const rows = Math.max(1, Math.round(height / fixedUnit))
        setDynamicRows(rows)
      }
    }
    recalculateRows()
    window.addEventListener('resize', recalculateRows)
    return () => window.removeEventListener('resize', recalculateRows)
  }, [fixedUnit, quote, image, isUltraWide, isWidescreen, isDesktop, isTablet])

  const studRangeImage = useMemo((): StudRange[] => {
    if (isDesktop) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: dynamicRows, columns: 6 },
          offset: { top: 0, right: 0, bottom: 0, left: 6 },
          bricks: [
            {
              row: image ? 3 : 4,
              column: 0,
              color: BrickColor.WarmYellow,
              snapPosition: 'top-left',
            },
          ],
        },
      ]
    }
    if (isTablet) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: dynamicRows, columns: 2 },
          offset: { top: 0, right: 0, bottom: 0, left: 2 },
          bricks: [{ row: 1, column: 0, color: BrickColor.WarmYellow, snapPosition: 'top-left' }],
        },
      ]
    } else {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: dynamicRows, columns: 1 },
          offset: { top: 0, right: 0, bottom: 0, left: 1 },
          bricks: [{ row: 1, column: 0, color: BrickColor.WarmYellow, snapPosition: 'top-left' }],
        },
      ]
    }
  }, [isTablet, isDesktop, image, dynamicRows])

  const getStudRange = (isRightSide: boolean): StudRange[] => {
    const studRange = studRangeImage.map((range) => ({
      ...range,
      offset: range.offset ? { ...range.offset } : undefined,
      bricks: range.bricks ? [...range.bricks] : [],
    }))
    studRange[0].bricks[0].color = BrickColor.EarthBlue
    if (visualsColors == 'YellowBackgroundGreenBrick')
      studRange[0].bricks[0].color = BrickColor.WarmYellow
    if (visualsColors == 'PaleBackgroundPinkBrick') studRange[0].bricks[0].color = BrickColor.Coral
    if (visualsColors == 'GreenBackgroundBrownBrick')
      studRange[0].bricks[0].color = BrickColor.Nougat

    if (isRightSide) {
      studRange[0].bricks = []
      if (studRange[0].offset) studRange[0].offset.left = 0
    }
    return studRange
  }

  const baseplateImageStyle = useMemo(
    () => (isTablet ? { width: 'fit-content' } : undefined),
    [isTablet]
  )

  return (
    <div className={`coral-quote-large ${image ? 'has-image' : ''}`}>
      <div className={'coral-quote-large__wrapper'} ref={wrapperRef}>
        <Baseplate
          className={'baseplate-large--fixed-unit'}
          fixedUnit={fixedUnit}
          studRanges={getStudRange(false)}
          style={baseplateImageStyle}
        >
          <div />
        </Baseplate>
        <div
          className={`coral-quote-large__frame-wrapper ${
            image ? 'has-image' : ''
          } coral-quote-large__visual-${visualsColors}`}
        >
          {image && (
            <div className={'coral-quote-large__frame-image'}>
              <Asset
                altText={'Placeholder Image'}
                className={'coral-quote-large__image'}
                lazyLoading={true}
                objectFit={'cover'}
                srcSetWidths={[400, 600, 800, 1200, 1600]}
                thumbnail={image}
              />
            </div>
          )}
          <div className={'coral-quote-large__frame-description'}>{quote}</div>
        </div>
        <Baseplate
          className={'baseplate-large--fixed-unit'}
          fixedUnit={fixedUnit}
          studRanges={getStudRange(true)}
          style={baseplateImageStyle}
        >
          <div />
        </Baseplate>
      </div>
      <div className={'coral-quote-large__creator'}>
        {quotedPerson.name && (
          <div className={'coral-quote-large_creator-name'}>{quotedPerson.name}</div>
        )}
        {quotedPerson.occupation && <div>{quotedPerson.occupation}</div>}
        {quotedPerson.school_district && <div>{quotedPerson.school_district}</div>}
      </div>
    </div>
  )
}
